// Test page component for taking the English placement test

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Question, AnswerSubmission, TestSubmission } from '../types';
import { questionsAPI, testsAPI } from '../services/api';

const Test: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsByLevel, setQuestionsByLevel] = useState<{ [level: string]: Question[] }>({});
  const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [questionId: number]: string }>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const levels = ['A1', 'A2', 'B1', 'B2', 'C1'];

  useEffect(() => {
    loadQuestions();
  }, []);

  // Scroll to top when level changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentLevelIndex]);

  const loadQuestions = async () => {
    try {
      const questionsData = await questionsAPI.getPlacementTestQuestions();
      setQuestions(questionsData);
      
      // Group questions by difficulty level
      const grouped = questionsData.reduce((acc, question) => {
        const level = question.difficulty_level || 'A1';
        if (!acc[level]) {
          acc[level] = [];
        }
        acc[level].push(question);
        return acc;
      }, {} as { [level: string]: Question[] });
      
      setQuestionsByLevel(grouped);
    } catch (err: any) {
      setError('Failed to load questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId: number, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleNextLevel = () => {
    if (currentLevelIndex < levels.length - 1) {
      setCurrentLevelIndex(prev => prev + 1);
    }
  };

  const handlePreviousLevel = () => {
    if (currentLevelIndex > 0) {
      setCurrentLevelIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length === 0) {
      setError('Please answer at least one question before submitting.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Debug: Log the answers state
      console.log('Answers state:', answers);
      console.log('Answers type:', typeof answers);
      console.log('Answers entries:', Object.entries(answers));
      
      // Check for function objects in answers
      for (const [questionId, answer] of Object.entries(answers)) {
        console.log(`Question ${questionId}: ${answer} (type: ${typeof answer})`);
        if (typeof answer === 'function') {
          console.error(`Function object found in answers for question ${questionId}:`, answer);
        }
      }

      const answerSubmissions: AnswerSubmission[] = Object.entries(answers).map(([questionId, answer]) => ({
        question_id: parseInt(questionId),
        answer,
      }));

      console.log('Answer submissions:', answerSubmissions);

      const testSubmission: TestSubmission = {
        answers: answerSubmissions,
      };

      console.log('Test submission:', testSubmission);

      const result = await testsAPI.submitTest(testSubmission);
      // Scroll to top before navigating to results
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(`/results/${result.id}`);
    } catch (err: any) {
      setError('Failed to submit test. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">No Questions Available</h1>
        <p className="text-gray-600 mb-6">
          The test questions are not available. Please contact an administrator.
        </p>
      </div>
    );
  }

  const currentLevel = levels[currentLevelIndex];
  const currentLevelQuestions = questionsByLevel[currentLevel] || [];
  const answeredQuestions = Object.keys(answers).length;
  const totalQuestions = questions.length;
  const levelProgress = ((currentLevelIndex + 1) / levels.length) * 100;

  const getLevelColor = (level: string) => {
    const colors: { [key: string]: string } = {
      A1: 'bg-red-100 text-red-800',
      A2: 'bg-orange-100 text-orange-800',
      B1: 'bg-yellow-100 text-yellow-800',
      B2: 'bg-blue-100 text-blue-800',
      C1: 'bg-green-100 text-green-800',
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">English Placement Test</h1>
        <div className="flex items-center justify-center space-x-4">
          <span className={`px-4 py-2 rounded-full text-lg font-semibold ${getLevelColor(currentLevel)}`}>
            Level {currentLevel}
          </span>
          <span className="text-gray-600">
            Level {currentLevelIndex + 1} of {levels.length}
          </span>
        </div>
      </div>

      {/* Progress Indicators */}
      <div className="mb-8">
        {/* Overall Progress */}
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            Overall Progress: {answeredQuestions} of {totalQuestions} questions answered
          </span>
          <span className="text-sm font-medium text-gray-700">
            {Math.round(levelProgress)}% Complete
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
          <div
            className="bg-primary-600 h-3 rounded-full transition-all duration-300"
            style={{ width: `${levelProgress}%` }}
          ></div>
        </div>

        {/* Level Progress */}
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            {currentLevel} Level: {currentLevelQuestions.filter(q => answers[q.id]).length} of {currentLevelQuestions.length} answered
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ 
              width: `${currentLevelQuestions.length > 0 ? (currentLevelQuestions.filter(q => answers[q.id]).length / currentLevelQuestions.length) * 100 : 0}%` 
            }}
          ></div>
        </div>
      </div>

      {/* Level Questions */}
      <div className="bg-white rounded-lg shadow-md p-8 mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          {currentLevel} Level Questions ({currentLevelQuestions.length} questions)
        </h2>
        
        <div className="space-y-8">
          {currentLevelQuestions.map((question, index) => (
            <div key={question.id} className="border-b border-gray-200 pb-6 last:border-b-0">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Question {index + 1}: {question.question_text}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { key: 'a', text: question.option_a },
                  { key: 'b', text: question.option_b },
                  { key: 'c', text: question.option_c },
                  { key: 'd', text: question.option_d },
                ]
                  .filter(option => option.text && option.text.trim() !== '')
                  .map((option) => (
                  <label
                    key={option.key}
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      answers[question.id] === option.key
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question-${question.id}`}
                      value={option.key}
                      checked={answers[question.id] === option.key}
                      onChange={() => handleAnswerSelect(question.id, option.key)}
                      className="sr-only"
                    />
                    <span className="flex-shrink-0 w-5 h-5 border-2 rounded-full flex items-center justify-center mr-3">
                      {answers[question.id] === option.key && (
                        <div className="w-2.5 h-2.5 bg-primary-600 rounded-full"></div>
                      )}
                    </span>
                    <span className="text-gray-900 text-sm">{option.text}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={handlePreviousLevel}
          disabled={currentLevelIndex === 0}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          ← Previous Level
        </button>

        <div className="flex space-x-4">
          {currentLevelIndex === levels.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {submitting ? 'Submitting...' : 'Submit Test'}
            </button>
          ) : (
            <button
              onClick={handleNextLevel}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              Next Level →
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
    </div>
  );
};

export default Test;


